import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatMoney, localized } from "../lib/format";

interface PnlLine {
  code: string;
  name: Record<string, string>;
  amountMinor: number;
}

interface Pnl {
  income: PnlLine[];
  expenses: PnlLine[];
  netProfitMinor: number;
}

interface Account {
  id: string;
  code: string;
  name: Record<string, string>;
  type: string;
  balanceMinor: number;
}

interface PendingReceipt {
  id: string;
  bookingId: string;
  receiptNo: string;
  method: string;
  amountMinor: number;
  note?: string;
}

export function FinancePage() {
  const { orgId } = useSession();
  const [pnl, setPnl] = useState<Pnl | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [pending, setPending] = useState<PendingReceipt[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function loadReports() {
    if (!orgId) return;
    api<Pnl>(`/orgs/${orgId}/finance/reports/pnl`).then(setPnl).catch((e) => setError(e.message));
    api<{ items: Account[] }>(`/orgs/${orgId}/finance/accounts`)
      .then((r) => setAccounts(r.items))
      .catch((e) => setError(e.message));
  }

  useEffect(loadReports, [orgId]);

  // Maker–checker: staff-recorded receipts wait here for the owner's verify.
  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, "payments"),
      where("orgId", "==", orgId),
      where("status", "==", "pending_verification"),
    );
    return onSnapshot(q, (snap) => {
      setPending(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PendingReceipt, "id">) })),
      );
    });
  }, [orgId]);

  async function verify(paymentId: string) {
    setBusyId(paymentId);
    try {
      await api(`/orgs/${orgId}/payments/${paymentId}/verify`, { method: "POST" });
      loadReports();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <Typography variant="h5" mb={2}>
        مالی — عاید و مصارف
      </Typography>
      {error && <Typography color="error">{error}</Typography>}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                مفاد خالص
              </Typography>
              <Typography variant="h5" fontWeight={700} color="primary">
                {pnl ? formatMoney(pnl.netProfitMinor) : "…"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                مجموع عاید
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {pnl ? formatMoney(pnl.income.reduce((s, l) => s + l.amountMinor, 0)) : "…"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                مجموع مصارف
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {pnl ? formatMoney(pnl.expenses.reduce((s, l) => s + l.amountMinor, 0)) : "…"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {pending.length > 0 && (
        <>
          <Typography variant="h6" mb={1}>
            رسیدهای در انتظار تأیید
          </Typography>
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>رسید</TableCell>
                  <TableCell>طریقه</TableCell>
                  <TableCell>مبلغ</TableCell>
                  <TableCell>یادداشت</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((receipt) => (
                  <TableRow key={receipt.id}>
                    <TableCell>{receipt.receiptNo}</TableCell>
                    <TableCell>{receipt.method === "cash" ? "نقد" : "بانک"}</TableCell>
                    <TableCell>{formatMoney(receipt.amountMinor)}</TableCell>
                    <TableCell>{receipt.note ?? "—"}</TableCell>
                    <TableCell align="left">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busyId === receipt.id}
                        onClick={() => verify(receipt.id)}
                      >
                        تأیید و ثبت در دفاتر
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* Platform settlements have their own page now — one table, one source. */}
      <Button component={RouterLink} to="/payouts" sx={{ mb: 3 }}>
        تسویه‌های پلتفرم (پرداخت‌های آنلاین) →
      </Button>

      <Typography variant="h6" mb={1}>
        حساب‌ها (Chart of Accounts)
      </Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>کد</TableCell>
              <TableCell>حساب</TableCell>
              <TableCell>نوع</TableCell>
              <TableCell align="left">بیلانس</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((acc) => (
              <TableRow key={acc.id}>
                <TableCell>{acc.code}</TableCell>
                <TableCell>{localized(acc.name)}</TableCell>
                <TableCell>{acc.type}</TableCell>
                <TableCell align="left">{formatMoney(Math.abs(acc.balanceMinor))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
}
